import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Package, Sparkles, Truck, Moon, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import WhatsAppButton from "@/components/WhatsAppButton";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-mystic-void flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-mystic-amethyst border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-mystic-void flex flex-col items-center justify-center">
        <Sparkles className="w-16 h-16 text-mystic-amethyst/30 mb-4" />
        <p className="font-serif text-2xl text-mystic-silver mb-4 italic">Crystal not found</p>
        <Link to="/products">
          <Button variant="outline" className="rounded-full border-mystic-amethyst/50 text-mystic-amethyst">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Shop
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mystic-void pt-24" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="py-6 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/products" 
            className="inline-flex items-center text-mystic-mist hover:text-mystic-amethyst transition-colors"
            data-testid="back-to-products"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Shop
          </Link>
        </div>
      </div>

      {/* Product Content */}
      <section className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Images */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden glass border border-white/10 mb-4 neon-glow-hover">
                <img
                  src={product.images?.[selectedImage] || "https://images.unsplash.com/photo-1545128309-686a71a99f76?w=800"}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              </div>
              {product.images?.length > 1 && (
                <div className="flex gap-3">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index
                          ? "border-mystic-amethyst neon-glow"
                          : "border-white/10 hover:border-mystic-amethyst/50"
                      }`}
                      data-testid={`product-thumbnail-${index}`}
                    >
                      <img
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              <div>
                <Badge className="mb-3 bg-mystic-amethyst/20 text-mystic-amethyst border-mystic-amethyst/30">
                  {product.category === 'healing' ? '✧ Healing Crystal' : '◆ Raw Stone'}
                </Badge>
                {product.featured && (
                  <Badge className="ml-2 bg-mystic-gold text-mystic-void border-0">
                    ✨ Featured
                  </Badge>
                )}
                <h1 
                  className="font-serif text-4xl sm:text-5xl font-light text-mystic-silver mt-2 italic text-glow"
                  data-testid="product-name"
                >
                  {product.name}
                </h1>
              </div>

              <p 
                className="text-3xl font-semibold text-mystic-cyan price-tag"
                data-testid="product-price"
              >
                {formatPrice(product.price)}
              </p>

              <p className="text-mystic-mist text-lg leading-relaxed" data-testid="product-description">
                {product.description}
              </p>

              {/* Stock Status */}
              <div className="flex items-center gap-2">
                {product.stock > 0 ? (
                  <>
                    <Check className="w-5 h-5 text-whatsapp" />
                    <span className="text-whatsapp font-medium">
                      In Stock ({product.stock} available)
                    </span>
                  </>
                ) : (
                  <span className="text-red-400 font-medium">Out of Stock</span>
                )}
              </div>

              {/* Product Details */}
              <div className="border-t border-white/10 pt-6 space-y-4">
                {product.benefits && (
                  <div className="flex items-start gap-3">
                    <Star className="w-5 h-5 text-mystic-gold mt-0.5" />
                    <div>
                      <p className="font-medium text-mystic-silver">Mystical Properties</p>
                      <p className="text-mystic-mist">{product.benefits}</p>
                    </div>
                  </div>
                )}
                {product.weight && (
                  <div className="flex items-start gap-3">
                    <Package className="w-5 h-5 text-mystic-amethyst mt-0.5" />
                    <div>
                      <p className="font-medium text-mystic-silver">Weight</p>
                      <p className="text-mystic-mist">{product.weight}</p>
                    </div>
                  </div>
                )}
                {product.origin && (
                  <div className="flex items-start gap-3">
                    <Truck className="w-5 h-5 text-mystic-cyan mt-0.5" />
                    <div>
                      <p className="font-medium text-mystic-silver">Origin</p>
                      <p className="text-mystic-mist">{product.origin}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Order Button */}
              <div className="pt-6">
                {product.stock > 0 ? (
                  <WhatsAppButton product={product} className="w-full sm:w-auto" />
                ) : (
                  <Button 
                    disabled 
                    className="w-full sm:w-auto rounded-full px-8 py-6 text-base bg-white/10 text-mystic-mist"
                  >
                    Out of Stock
                  </Button>
                )}
                <p className="text-mystic-mist/60 text-sm mt-3">
                  Click to order directly via WhatsApp
                </p>
              </div>

              {/* Shipping Info */}
              <div className="glass rounded-2xl p-6 mt-8 border border-white/10">
                <h3 className="font-serif text-lg text-mystic-silver mb-3 italic flex items-center gap-2">
                  <Moon className="w-5 h-5 text-mystic-gold" />
                  Shipping & Care
                </h3>
                <ul className="space-y-2 text-mystic-mist text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-mystic-amethyst" />
                    Free shipping on orders above ₹1,500
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-mystic-amethyst" />
                    Moon-charged before shipping
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-mystic-amethyst" />
                    Ships to all pincodes across India
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-mystic-amethyst" />
                    Sacred packaging with care instructions
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
